<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            {{ added ? 'Create New Episode' : 'Edit Episode' }}
          </h1>
        </div>
        <div class="columns is-vcentered">
          <div class="column is-6 is-offset-3">
            <div class="flex-card light-bordered light-raised">
              <div class="card-body">
                <ValidationObserver ref="observer" v-slot="{ passes }">
                  <form @submit.prevent="passes(submit)">
                    <div class="columns is-multiline">
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Title"
                            rules="required"
                          >
                            <b-field
                              label="Title"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.title" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Description"
                          >
                            <b-field
                              label="Description"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.description"
                                type="textarea"
                              />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Episode No"
                            rules="required"
                          >
                            <b-field
                              label="Episode No"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.episodeNumber"
                                type="number"
                              />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Length in Minutes"
                          >
                            <b-field label="Length in Minutes">
                              <b-input
                                v-model="item.episodeLength"
                                type="number"
                                rules="required"
                              />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Release Date"
                          >
                            <b-field label="Release Date">
                              <b-datepicker
                                v-model="item.releaseDate"
                                placeholder="Click to select..."
                                icon="calendar-today"
                              >
                              </b-datepicker>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Age Restriction"
                          >
                            <b-field
                              label="Age Restriction"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.ageRestriction" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <BaseDateTimePicker
                          v-model="item.vodStartDate"
                          label="VOD Start Date"
                        >
                        </BaseDateTimePicker>
                      </div>
                      <div class="column is-6-desktop">
                        <BaseDateTimePicker
                          v-model="item.vodExpiryDate"
                          :min-datetime="item.vodStartDate"
                          label="VOD Expiry Date"
                        >
                        </BaseDateTimePicker>
                      </div>
                      <div class="column is-6-desktop" v-if="!added">
                        <div class="control">
                          <UploadMediaAssets
                            :episode-id="item.id"
                            :media-id="item.mediaId"
                          ></UploadMediaAssets>
                        </div>
                      </div>
                    </div>
                    <div class="has-text-centered">
                      <div class="is-block has-margin-bottom-15">
                        <b-button
                          icon-left="arrow-left"
                          class="is-medium has-margin-right-10"
                          @click="backToManageEpisodes"
                        >
                          Back to List</b-button
                        >
                        <button
                          class="button is-primary is-medium btn-align no-lh raised primary-btn"
                          type="submit"
                          :disabled="loading"
                          :class="{ 'is-loading': loading }"
                        >
                          {{ added ? 'Create' : 'Update' }}
                        </button>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </CardComponent>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import MediaApi from '@/api/media'
import EpisodeApi from '@/api/episodes'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import UploadMediaAssets from '@/components/UploadMediaAssets'

export default {
  name: 'EditMedia',
  components: {
    CardComponent,
    BaseDateTimePicker,
    UploadMediaAssets,
  },
  metaInfo: {
    title: 'Edit Episode',
  },
  computed: {
    ...mapGetters(['user']),
  },
  watch: {
    async $route() {
      if (!this.added) {
        await this.setData()
      }
    },
  },
  async mounted() {
    this.added = this.$route.name === 'CreateEpisode' ? true : false
    if (!this.added) {
      await this.setData()
    }
    this.initialized = true
  },
  data() {
    return {
      loading: false,
      initialized: false,
      added: false,
      item: {},
    }
  },
  methods: {
    ...mapActions(['getUser']),
    backToManageEpisodes() {
      this.$router.push({
        name: 'ManageEpisodes',
        params: {
          id: this.added ? this.$route.params.id : this.item.mediaId,
        },
      })
    },
    async setData() {
      let episode = await EpisodeApi.get(this.$route.params.id)
      if (episode) {
        this.item.id = episode.id
        this.item.title = episode.title
        this.item.description = episode.description
        this.item.episodeNumber = episode.episode_number
        this.item.mediaId = episode.media_id
        if (episode.episodedata && episode.episodedata !== '') {
          episode.episodedata = JSON.parse(episode.episodedata)
          if (episode.episodedata.releaseDate) {
            this.item.releaseDate = new Date(episode.episodedata.releaseDate)
          }
          this.item.ageRestriction = episode.episodedata.ageRestriction
          this.item.episodeLength = episode.episodedata.episodeLength
          this.item.vodStartDate = episode.episodedata.vodStartDate
            ? new Date(episode.episodedata.vodStartDate)
            : undefined
          this.item.vodExpiryDate = episode.episodedata.vodExpiryDate
            ? new Date(episode.episodedata.vodExpiryDate)
            : undefined
        }
      }
    },

    submit() {
      this.item.episodedata = {
        releaseDate: this.item.releaseDate,
        episodeLength: this.item.episodeLength,
        vodStartDate: this.item.vodStartDate,
        vodExpiryDate: this.item.vodExpiryDate,
        ageRestriction: this.item.ageRestriction,
      }
      this.loading = true
      if (!this.item.id) {
        this.item.mediaId = this.$route.params.id
        EpisodeApi.create(this.item)
          .then((response) => {
            this.$buefy.toast.open({
              message: 'Episode created successfully!',
              position: 'is-top',
              type: 'is-success',
            })
            this.$router.push({
              name: 'ManageEpisodes',
              params: { id: this.$route.params.id },
            })
            this.loading = false
          })
          .catch((err) => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving episode',
              position: 'is-top',
              type: 'is-danger',
            })
            this.loading = false
          })
      } else {
        this.item.episodeId = this.item.id
        EpisodeApi.update(this.item)
          .then(() => {
            this.$buefy.toast.open({
              message: 'Episode updated successfully!',
              position: 'is-top',
              type: 'is-success',
            })
            this.setData()
            this.loading = false
          })
          .catch((err) => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving episode',
              position: 'is-top',
              type: 'is-danger',
            })
            this.loading = false
          })
      }
    },
  },
}
</script>
